import { useConfigStore } from '@/store/config';
import { usePairs } from '@/store/pairs';
import { useTradeStore } from '@/store/trade-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { convertValueToNumber, stringToBigNumber } from '@/utils/formula';
import { parseValue } from '@/utils/numbers';
import BigNumber from 'bignumber.js';
import { t } from 'i18next';
import { shallow } from 'zustand/shallow';
import { FormValue, Type } from '../type';
import { useBalance } from '@/hooks/useBalance';

const MIN_VOLUME_DEFAULT = 150;

export function useValidate() {
    const { control, getValues } = useFormContext<FormValue>();

    const [isStartValidate, setIsStartValidate] = useState(true);

    const [error, setError] = useState<string | undefined | null>(null);

    const [fees] = useConfigStore((state) => [state.fees]);

    const [booking, orderTrade] = useTradeStore(
        (state) => [state.booking, state.orderTrade],
        shallow
    );
    const pairs = usePairs((state) => state.data);

    const { data: usdcBalance } = useBalance();

    const [pairIndex, amount, leverage] = useWatch({
        control: control,
        name: ['pairIndex', 'amount', 'leverage'],
    });

    const pairTrade = pairs.find(
        (item) => item.pairIndex === Number(pairIndex)
    );

    const selectedTokenConfig = useMemo(() => {
        if (!pairTrade) {
            return { maxOpenInterest: 0 };
        }

        if (!pairs) {
            return { maxOpenInterest: 0 };
        }

        return pairs.find((item) => item.pairIndex === pairTrade.pairIndex);
    }, [pairs, pairTrade]);

    const { openInterstLong, openInterestShort, maxOpenInterest } =
        useMemo(() => {
            const fundingToToken = booking?.find(
                (x) => x.pair_index?.toString() === pairIndex?.toString()
            );
            return {
                openInterstLong: convertValueToNumber(
                    fundingToToken?.open_interest_long ?? 0,
                    18
                ),
                openInterestShort: convertValueToNumber(
                    fundingToToken?.open_interest_short ?? 0,
                    18
                ),
                maxOpenInterest: convertValueToNumber(
                    fundingToToken?.max_open_interest ??
                        selectedTokenConfig?.maxOpenInterest ??
                        0,
                    0
                ),
            };
        }, [booking, pairIndex, selectedTokenConfig?.maxOpenInterest]);

    const { totalOpenInterestLong, totalOpenInterestShort } = useMemo(() => {
        let totalOIL = 0;
        let totalOIS = 0;
        const openTradePair = orderTrade.openTrade.filter(
            (x) =>
                x.trade_info.pair_index?.toString() ===
                pairTrade?.pairIndex.toString()
        );
        const pendingTradePair = orderTrade.pendingTrade.filter(
            (x) =>
                x.trade_info.pair_index?.toString() ===
                pairTrade?.pairIndex.toString()
        );
        for (let i = 0; i < Number(openTradePair.length); i++) {
            const element = openTradePair[i];
            const posAdd =
                convertValueToNumber(element.trade_info.initial_pos_token, 18) *
                Number(element.trade_info.leverage);
            if (element.trade_info.buy) {
                totalOIL += posAdd;
            } else {
                totalOIS += posAdd;
            }
        }
        for (let i = 0; i < pendingTradePair?.length; i++) {
            const element = pendingTradePair[i];
            const posAdd =
                convertValueToNumber(element.trade_info.initial_pos_token, 18) *
                Number(element.trade_info.leverage);
            if (element.trade_info.buy) {
                totalOIL += posAdd;
            } else {
                totalOIS += posAdd;
            }
        }
        return {
            totalOpenInterestLong: totalOIL,
            totalOpenInterestShort: totalOIS,
        };
    }, [orderTrade.openTrade, orderTrade.pendingTrade, pairTrade?.pairIndex]);
    const validateOnchange = useCallback(() => {
        const fromAmount = parseValue(amount, 18);

        if (!amount) {
            return t('Enter an amount');
        }

        if (
            new BigNumber(usdcBalance?.toString() ?? '0')
                .dividedBy(1e18)
                .lt(amount)
        ) {
            return t('Not enough balance');
        }

        const minLevPosDaiConfig = [92, 110].includes(pairTrade?.pairIndex || 0)
            ? 250000000000000000000
            : fees?.[pairTrade?.group || 0]?.minLevPosDai;

        if (
            stringToBigNumber(
                (Number(leverage) * Number(amount)).toFixed(2),
                18
            )?.lt(new BigNumber(minLevPosDaiConfig ?? MIN_VOLUME_DEFAULT))
        ) {
            const minLevPosDai =
                (Number(minLevPosDaiConfig) || 0) / Math.pow(10, 18) ||
                MIN_VOLUME_DEFAULT;

            return `${t('Min position size is')} ${minLevPosDai}`;
        }
        // (leverage * amount) * 1e18
        if (
            typeof pairTrade?.maxPosition == 'number' &&
            stringToBigNumber(
                (Number(leverage) * Number(amount)).toFixed(2),
                18
            ).gt(new BigNumber(pairTrade.maxPosition).multipliedBy(1e18))
        ) {
            return `${t('Max position size is')} ${pairTrade?.maxPosition}`;
        }
        if (
            fromAmount &&
            fromAmount.gt(new BigNumber(usdcBalance?.toString() || 0))
        ) {
            return t('Insufficient Balance');
        }
    }, [
        amount,
        fees,
        leverage,
        pairTrade?.group,
        pairTrade?.maxPosition,
        pairTrade?.pairIndex,
        usdcBalance,
    ]);

    const validateForm = useCallback(
        (typeTrade: Type, isValidateOnchange = true) => {
            if (isValidateOnchange) {
                setIsStartValidate(true);

                const errorOnchange = validateOnchange();

                if (errorOnchange) {
                    return errorOnchange;
                }
            }

            const { amount, leverage } = getValues();

            // if (
            //     count.openTrade + count.pendingTrade >=
            //     Number(maxTradesPerPair || 5)
            // ) {
            //     return `${t('Max open orders on this pair')}`;
            // }

            if (
                typeTrade === Type.Long &&
                maxOpenInterest &&
                Number(leverage) * Number(amount) + openInterstLong >
                    maxOpenInterest
            ) {
                return `${t('Pair position size limit reached')}`;
            }

            if (
                typeTrade !== Type.Long &&
                maxOpenInterest &&
                Number(leverage) * Number(amount) + openInterestShort >
                    maxOpenInterest
            ) {
                return `${t('Pair position size limit reached')}`;
            }

            if (
                typeTrade === Type.Long &&
                Number(leverage) * Number(amount) + totalOpenInterestLong >
                    maxOpenInterest / 2
            ) {
                return `${t('Pair position size limit reached')}`;
            }

            if (
                typeTrade !== Type.Long &&
                Number(leverage) * Number(amount) + totalOpenInterestShort >
                    maxOpenInterest / 2
            ) {
                return `${t('Pair position size limit reached')}`;
            }

            return '';
        },
        [
            getValues,
            maxOpenInterest,
            openInterestShort,
            openInterstLong,
            totalOpenInterestLong,
            totalOpenInterestShort,
            validateOnchange,
        ]
    );

    useEffect(() => {
        if (!isStartValidate) {
            return;
        }

        const errorOnchange = validateOnchange();

        if (errorOnchange) {
            // You may want to set an error state here, e.g. setError(errorOnchange);
            setError(errorOnchange);
        } else {
            setError((prev) => (prev !== null ? undefined : null));
        }
    }, [isStartValidate, validateOnchange]);

    return { validateForm, errorOnchange: error, isStartValidate };
}
